import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatTZS, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Clock, CheckCircle2, XCircle, Users, Calendar } from "lucide-react";

const badgeClass = {
  pending: "bg-amber-100 text-amber-700",
  quoted: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};

export default function QuoteStatus() {
  const { reference } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/service-quotes/reference/${reference}`); setQuote(data); }
    catch (e) { toast.error(formatApiError(e) || "Quote not found"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reference]);

  const act = async (action) => {
    setSaving(true);
    try {
      await api.post(`/service-quotes/reference/${reference}/${action}`);
      toast.success(action === "accept" ? "Quote accepted! Reception will follow up." : "Quote declined.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[color:var(--sand)]"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand)]" /></div>;
  if (!quote) return <div className="min-h-screen flex items-center justify-center bg-[color:var(--sand)]"><p className="font-ui text-[color:var(--muted-text)]">Quote not found</p></div>;

  const canAct = quote.status === "quoted";

  return (
    <div className="min-h-screen bg-[color:var(--sand)] py-16 px-4" data-testid="quote-status-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-ui text-[color:var(--deep)] hover:text-[color:var(--brand)] mb-6"><ArrowLeft className="w-4 h-4" /> Back to Baraka Inn</Link>
        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-[color:var(--brand)]/10">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Quote {quote.reference}</p>
              <h1 className="font-heading text-3xl sm:text-4xl text-[color:var(--deep)] mt-1">{quote.service_title}</h1>
            </div>
            <Badge className={`${badgeClass[quote.status] || ""} border-0 capitalize`} data-testid="quote-status">{quote.status}</Badge>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 font-ui text-sm">
            <div className="flex items-center gap-2 text-[color:var(--deep)]"><Users className="w-4 h-4 text-[color:var(--brand)]" /> {quote.guests} guests</div>
            <div className="flex items-center gap-2 text-[color:var(--deep)]"><Calendar className="w-4 h-4 text-[color:var(--brand)]" /> {formatDate(quote.event_date)}</div>
          </div>
          {quote.menu_notes && <p className="mt-4 text-sm bg-[color:var(--sand)] rounded-xl p-4"><b>Menu:</b> {quote.menu_notes}</p>}
          {quote.requirements && <p className="mt-2 text-sm bg-[color:var(--sand)] rounded-xl p-4"><b>Requirements:</b> {quote.requirements}</p>}

          <div className="mt-8 p-6 rounded-2xl bg-[color:var(--sand)] border border-[color:var(--brand)]/15">
            {quote.status === "pending" && (
              <div className="flex items-center gap-3 text-[color:var(--deep)]">
                <Clock className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-heading text-lg">Reception is preparing your quote</p>
                  <p className="text-sm text-[color:var(--muted-text)]">We'll reply within a few hours. Check this page again or call us.</p>
                </div>
              </div>
            )}
            {quote.status === "quoted" && (
              <>
                <p className="font-ui text-xs uppercase tracking-wider text-[color:var(--brand)]">Your price</p>
                <p className="font-heading text-4xl text-[color:var(--deep)] mt-1" data-testid="quote-price">{formatTZS(quote.quoted_price)}</p>
                {quote.quote_notes && <p className="mt-3 text-sm text-[color:var(--deep)] whitespace-pre-wrap">{quote.quote_notes}</p>}
              </>
            )}
            {quote.status === "accepted" && (
              <div className="flex items-center gap-3 text-emerald-700">
                <CheckCircle2 className="w-6 h-6" />
                <div>
                  <p className="font-heading text-lg">Quote accepted — see you soon!</p>
                  <p className="text-sm">Reception will contact you to confirm details.</p>
                </div>
              </div>
            )}
            {quote.status === "declined" && (
              <div className="flex items-center gap-3 text-red-700">
                <XCircle className="w-6 h-6" />
                <p className="font-heading text-lg">Quote declined</p>
              </div>
            )}
          </div>

          {canAct && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => act("accept")} disabled={saving} data-testid="quote-accept-btn" className="flex-1 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)] h-12 rounded-full">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Accept quote
              </Button>
              <Button onClick={() => act("decline")} disabled={saving} variant="outline" data-testid="quote-decline-btn" className="flex-1 h-12 rounded-full">
                <XCircle className="w-4 h-4 mr-2" /> Decline
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
